import { useCallback, useState } from "react";
import { Button, Flex, TextArea, Text, Box, ScrollArea } from "@radix-ui/themes";
import { Dialog } from "@/components/shared/dialog";
import { HumanAssistRequest } from "@/v2/entities/approval-request";

interface AnswerAssistModalProps {
  request: HumanAssistRequest;
  onClose: () => void;
  onAnswer: (answer: string) => void;
  isSubmitting: boolean;
}

export function AnswerAssistModal({
  request,
  onClose,
  onAnswer,
  isSubmitting,
}: AnswerAssistModalProps) {
  const [answer, setAnswer] = useState("");

  const handleAnswerChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setAnswer(e.target.value);
    },
    []
  );

  const handleSubmit = useCallback(() => {
    if (answer.trim()) {
      onAnswer(answer.trim());
    }
  }, [answer, onAnswer]);

  return (
    <Dialog open onOpenChange={onClose}>
      <Dialog.Content size="large">
        <Flex direction="column" gap="4" p="4">
          <Flex justify="between" align="center">
            <Text size="5" weight="bold">
              Answer AI Assist Request
            </Text>
            <Dialog.Close />
          </Flex>

          <Box>
            <Text size="2" weight="medium" color="gray">
              Original User Query
            </Text>
            <Box
              mt="1"
              p="3"
              style={{
                backgroundColor: "var(--gray-a3)",
                borderRadius: "var(--radius-2)",
              }}
            >
              <Text size="2">{request.originalUserQuery}</Text>
            </Box>
          </Box>

          <Box>
            <Text size="2" weight="medium" color="gray">
              AI&apos;s Question for You
            </Text>
            <Box
              mt="1"
              p="3"
              style={{
                backgroundColor: "var(--accent-a3)",
                borderRadius: "var(--radius-2)",
              }}
            >
              <Text size="2" weight="medium">
                {request.questionForHuman}
              </Text>
            </Box>
          </Box>

          <Box>
            <Text size="2" weight="medium" color="gray">
              AI Context (what the AI found so far)
            </Text>
            <ScrollArea
              type="auto"
              scrollbars="vertical"
              style={{ maxHeight: "150px" }}
            >
              <Box
                mt="1"
                p="3"
                style={{
                  backgroundColor: "var(--gray-a2)",
                  borderRadius: "var(--radius-2)",
                }}
              >
                <Text size="1" color="gray">
                  {request.aiContext || "No additional context provided."}
                </Text>
              </Box>
            </ScrollArea>
          </Box>

          <Box>
            <Text size="2" weight="medium" mb="1">
              Your Answer
            </Text>
            <TextArea
              value={answer}
              onChange={handleAnswerChange}
              placeholder="Type your answer here. The AI will use this to respond to the user..."
              rows={5}
              variant="soft"
              color="gray"
              autoFocus
              disabled={isSubmitting}
            />
          </Box>

          <Flex gap="2" justify="end">
            <Button
              variant="outline"
              color="gray"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="green"
              highContrast
              onClick={handleSubmit}
              disabled={!answer.trim() || isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Answer"}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
